// ---------- i18n : point d'entrée ----------
// Deux dictionnaires (`fr`, `en`), la langue active choisie une fois au chargement du module et
// gardée pendant toute la session — voir setLang() plus bas pour pourquoi un changement recharge
// la page plutôt que de retraduire en direct.

import fr, { type I18nKey } from './fr';
import en from './en';
import es from './es';
import pt from './pt';
import de from './de';
import { alphabetById } from '../../shared/alphabets';
import { diffLabel } from '../../shared/difficulty';
import { INKS } from '../../shared/ink';

export type { I18nKey };
export type Lang = 'fr' | 'en' | 'es' | 'pt' | 'de';

/** Les dictionnaires, et la LISTE DES LANGUES : `LANGS` en est déduite plutôt que redéclarée à
 *  côté, sinon les deux divergent au premier ajout — c'est exactement le genre de doublon qui
 *  laisse une langue traduite mais inatteignable depuis les réglages. */
const DICTS = { fr, en, es, pt, de } as const satisfies Record<Lang, Record<I18nKey, string>>;
export const LANGS = Object.keys(DICTS) as Lang[];

/** Le nom de chaque langue DANS ELLE-MÊME, jamais traduit vers la langue active : quelqu'un
 *  coincé dans une langue qu'il ne lit pas cherche « Deutsch » à l'écran, pas « Allemand ».
 *  `satisfies` : ajouter une langue sans lui donner son nom ne compile pas. */
export const LANG_NAMES = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
} as const satisfies Record<Lang, string>;

/**
 * L'accroche du jeu DANS une langue donnée — la seule chose que le module rende d'un dictionnaire
 * qui n'est pas celui de la session.
 *
 * Elle existe pour le sélecteur de langue : le reste des réglages se choisit en VOYANT (une
 * pastille d'encre montre son encre, une pastille de thème montre son thème), et une langue se
 * montre en parlant. Deux clés et non une phrase à part : ce sont les deux lignes du splash, déjà
 * traduites et déjà relues, et une accroche de plus à tenir en cinq langues serait une occasion de
 * plus de les laisser diverger.
 */
export function taglineIn(l: Lang): string {
  return `${DICTS[l]['splash.line1']} ${DICTS[l]['splash.line2']}`;
}

const LANG_KEY = 'codigrid:lang:v1';
/** Le repli quand l'appareil ne demande aucune des langues connues. L'anglais et non le
 *  français : c'est la langue que le plus de monde lit à défaut de la sienne. */
const DEFAULT_LANG: Lang = 'en';

function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (LANGS as string[]).includes(v);
}

function readStored(): string | null {
  try {
    return localStorage.getItem(LANG_KEY);
  } catch {
    return null;
  }
}

/**
 * La langue à parler, dans l'ordre : le CHOIX du joueur, puis ce que son appareil demande, puis
 * l'anglais.
 *
 * Séparée en fonction pure et exportée pour être testable : la langue effective est calculée une
 * seule fois au chargement du module, donc sans cette porte d'entrée il n'y aurait aucun moyen de
 * vérifier l'ordre des règles autrement qu'en truquant `navigator` avant l'import.
 *
 * Les étiquettes reçues sont des balises BCP 47 (`pt-BR`, `es-419`, `de-AT`) : seule la
 * sous-étiquette primaire compte ici. Le portugais du Brésil et celui du Portugal partagent donc
 * le même dictionnaire — écrit pour le Brésil, cf. l'en-tête de pt.ts.
 */
export function pickLang(stored: string | null, preferred: readonly string[]): Lang {
  if (isLang(stored)) return stored;
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return DEFAULT_LANG;
}

/**
 * Ce que l'appareil demande, dans l'ordre de préférence.
 *
 * Le garde sur `document` n'est pas de la prudence décorative : Node expose LUI AUSSI un
 * `navigator.language`, celui de la machine, et le lire sous les tests ferait dépendre la suite
 * de la locale du poste — verte à Paris, rouge sur un agent d'intégration en anglais. Hors
 * navigateur il n'y a personne dont on puisse détecter la langue, et rendre une liste vide est la
 * réponse exacte à cette question.
 */
function devicePrefs(): readonly string[] {
  if (typeof document === 'undefined') return [];
  const n = globalThis.navigator;
  if (!n) return [];
  return n.languages?.length ? n.languages : n.language ? [n.language] : [];
}

const lang: Lang = pickLang(readStored(), devicePrefs());
const dict: Record<I18nKey, string> = DICTS[lang];

export function currentLang(): Lang {
  return lang;
}

/**
 * Enregistre le choix et RECHARGE LA PAGE plutôt que de retraduire l'écran courant — même
 * mécanisme déjà accepté du panneau pour un changement qui touche large (wipe() et le dialogue
 * de mise à jour font tous les deux `location.reload()`). Sans danger pour la partie en cours :
 * les taps en vol sont vidés sur `pagehide` (cf. wireProgressFlush), un rechargement ne perd pas
 * plus qu'une mise à jour de build ne le fait déjà.
 *
 * L'autre raison, plus décisive : une partie du texte du jeu est calculée UNE FOIS au chargement
 * du module et mise en cache (mark.ts, les libellés de home.ts) — la retraduire en direct
 * demanderait de transformer ces constantes en fonctions recalculées à la demande, dans
 * plusieurs fichiers, pour économiser une seconde de rechargement. Un rechargement réexécute ces
 * modules avec la bonne langue déjà active avant le premier rendu.
 */
export function setLang(next: Lang): void {
  if (next === lang) return;
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch {
    /* pas de stockage : le choix ne survit pas à cette session, tant pis */
  }
  location.reload();
}

/**
 * Lit une clé du dictionnaire actif et remplace ses `{{nom}}` par les valeurs fournies. `key`
 * est typée contre les clés réelles de `fr` : une clé qui n'existe pas est une ERREUR DE
 * COMPILATION, jamais un texte manquant découvert en production.
 */
export function t(key: I18nKey, vars?: Record<string, string | number>): string {
  // Une clé absente rend une chaîne vide, pas `undefined` : le cas n'arrive que pour une clé
  // CONSTRUITE (`badge.${id}`) avec un identifiant que le serveur a ajouté avant que l'APK ne le
  // connaisse — le texte « undefined » s'affichait, et avec des variables, `replaceAll` plantait.
  // Les clés écrites en dur, elles, sont vérifiées par le compilateur et par tests/i18n.test.ts.
  let s: string = dict[key] ?? '';
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{{${k}}}`, String(v));
  return s;
}

/**
 * Une durée en minutes, mise en mots. L'entrée vient de `estimateMinutes` (shared/effort.ts) et
 * tombe donc toujours sur un palier de son échelle : 5 à 45 min, 1 h, 1 h 30, puis 120 qui est un
 * PLANCHER et se lit « 2 h et plus ».
 *
 * Ici et pas dans `shared/` : ce module-là est lu par l'administration, qui reste en français pour
 * toujours, et il ne rend donc que des nombres. C'est la même frontière que pour les trois
 * registres ci-dessous.
 */
/**
 * La durée telle que l'HORLOGE de la carte l'écrit : un nombre et une unité, sur deux lignes,
 * dans un cadran de 50 px.
 *
 * L'écriture longue n'y tient pas — « 1 h 30 » sur une ligne de 17 px déborde du cercle, mesuré.
 * Au-delà de l'heure on passe donc à l'heure comme unité, et les deux seuls paliers concernés
 * s'écrivent avec une fraction : `1½` et `2+`. Le glyphe ne se traduit pas et se lit dans les
 * cinq langues, là où « 1 h 30 » aurait demandé une abréviation par langue (l'allemand écrit
 * « Std. »).
 *
 * Les paliers viennent de `estimateMinutes` (shared/effort.ts) : 5 à 45, puis 60, 90, 120.
 */
export function dialParts(m: number): { n: string; u: string } {
  if (m >= 120) return { n: `${Math.floor(m / 60)}+`, u: t('time.unit.h') };
  if (m > 60) return { n: `${Math.floor(m / 60)}\u00bd`, u: t('time.unit.h') };
  if (m === 60) return { n: '1', u: t('time.unit.h') };
  return { n: String(m), u: t('time.unit.min') };
}

export function fmtMinutes(m: number): string {
  if (m >= 120) return t('time.hplus', { n: Math.floor(m / 60) });
  if (m < 60) return t('time.min', { n: m });
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? t('time.hmin', { h, m: rest }) : t('time.h', { n: h });
}

// ---------- le calendrier du puzzle du jour ----------
// Les noms de mois et de jours ne sont PAS dans les dictionnaires : `Intl` les connaît déjà dans
// les cinq langues, et les recopier à la main serait soixante chaînes à traduire, à accorder et à
// relire pour un résultat moins bon (Intl suit aussi les conventions de casse et d'ordre).

/**
 * Le premier jour de la semaine, par langue. UNE TABLE et non `Intl.Locale.getWeekInfo()`, qui
 * serait la réponse élégante : cette API n'est pas garantie sur une WebView Android déjà
 * installée, et un calendrier qui démarre un jour au hasard est pire qu'un calendrier qui démarre
 * lundi partout. Ici le choix est explicite, lisible, et testable sans ICU.
 *
 * `satisfies Record<Lang, 1 | 7>` : ajouter une langue sans lui donner son premier jour ne
 * compile pas — même garde que LANG_NAMES.
 *
 * 1 = lundi, 7 = dimanche (numérotation ISO). L'anglais démarre dimanche parce que l'anglais du
 * jeu est celui de la fiche du Store, américain ; c'est UNE LIGNE à changer si on décide un jour
 * de viser le Royaume-Uni.
 */
export const WEEK_START = {
  fr: 1,
  en: 7,
  es: 1,
  pt: 1,
  de: 1,
} as const satisfies Record<Lang, 1 | 7>;

/** Le titre du calendrier : « septembre 2026 », dans la langue active. Première lettre en
 *  capitale — `Intl` rend le mois en bas de casse dans les langues romanes, et un titre d'écran
 *  qui commence en minuscule se lit comme un bout de phrase coupé. La bascule de casse passe par
 *  la locale (le turc ne majuscule pas le `i` comme nous ; le jeu n'y est pas encore, la ligne
 *  ne coûte rien). */
export function monthTitle(d: Date): string {
  const s = new Intl.DateTimeFormat(lang, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
  return s.charAt(0).toLocaleUpperCase(lang) + s.slice(1);
}

/**
 * Les sept initiales de jours, DANS L'ORDRE de la semaine locale — l'en-tête des colonnes du
 * calendrier.
 *
 * Elles partent d'un dimanche réel (le 4 janvier 1970 en est un) et non d'une date arbitraire à
 * laquelle on ajouterait un décalage : c'est ce qui rend la rotation évidente à relire. `narrow`
 * plutôt que `short` : sept colonnes sur un téléphone de 360 px, une lettre par colonne.
 */
export function weekdayInitials(): string[] {
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' });
  const DIMANCHE = Date.UTC(1970, 0, 4);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    // WEEK_START vaut 1 (lundi) ou 7 (dimanche) : le décalage depuis dimanche est donc 1 ou 0.
    const shift = WEEK_START[lang] === 7 ? 0 : 1;
    out.push(fmt.format(new Date(DIMANCHE + (i + shift) * 86400000)));
  }
  return out;
}

/**
 * La date de la vedette du quotidien : « Samedi 5 septembre ». SANS l'année, contrairement à
 * `dayLabel` — le titre du mois la porte deux lignes plus bas, et la répéter allongeait la
 * ligne juste assez pour qu'elle passe sur deux lignes en allemand.
 *
 * Capitale initiale pour la même raison que `monthTitle` : `Intl` rend le jour en bas de casse
 * dans les langues romanes, et une vedette qui commence en minuscule se lit comme une phrase
 * coupée.
 */
export function dayTitle(day: string): string {
  const s = new Intl.DateTimeFormat(lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
  return s.charAt(0).toLocaleUpperCase(lang) + s.slice(1);
}

/** La date d'une case, en entier et dans la langue active — pour l'infobulle et le lecteur
 *  d'écran, jamais pour la case elle-même, qui n'affiche que son numéro. */
export function dayLabel(day: string): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: 'long', timeZone: 'UTC' }).format(
    new Date(`${day}T00:00:00Z`),
  );
}

// ---------- les trois registres partagés avec l'admin ----------
// shared/alphabets, shared/difficulty.ts et shared/ink.ts exportent des libellés français que
// l'admin affiche AUSSI, en dur, pour toujours — shared/ ne devient donc jamais multilingue.
// Ces trois tables sont le SEUL endroit que le jeu doit changer pour une langue de plus sur ces
// trois registres. Le français n'y a PAS d'entrée : il relit le libellé partagé, comportement
// strictement identique à un accès direct à `.label`, zéro texte dupliqué. Une langue dont la
// table manque un identifiant retombe sur la même chaîne — un alphabet ajouté en administration
// s'affiche sous son libellé français plutôt que de disparaître de l'écran.
type Registre = Partial<Record<Lang, Record<string, string>>>;

const ALPHABET_LABELS: Registre = {
  en: {
    motifs: 'Patterns',
    motifsFin: 'Fine patterns',
    motifsTrait: 'Line patterns',
    asciiElargi: 'Extended ASCII',
    hachuresPleines: 'Solid hatching',
    pointille: 'Stippling',
    pointilleElargi: 'Extended stippling',
    pointilleRiche: 'Rich stippling',
    desPlein: 'Solid dice',
    briques: 'Bricks',
    peinture: 'Brushstrokes',
    peintureNuancee: 'Nuanced brushstrokes',
    briquesNuancees: 'Nuanced bricks',
    pointDeCroix: 'Cross stitch',
  },
  es: {
    motifs: 'Patrones',
    motifsFin: 'Patrones finos',
    motifsTrait: 'Patrones de línea',
    asciiElargi: 'ASCII ampliado',
    hachuresPleines: 'Tramado sólido',
    pointille: 'Puntillismo',
    pointilleElargi: 'Puntillismo ampliado',
    pointilleRiche: 'Puntillismo rico',
    desPlein: 'Dados sólidos',
    briques: 'Ladrillos',
    peinture: 'Pinceladas',
    peintureNuancee: 'Pinceladas matizadas',
    briquesNuancees: 'Ladrillos matizados',
    pointDeCroix: 'Punto de cruz',
  },
  pt: {
    motifs: 'Padrões',
    motifsFin: 'Padrões finos',
    motifsTrait: 'Padrões de linha',
    asciiElargi: 'ASCII estendido',
    hachuresPleines: 'Hachura sólida',
    pointille: 'Pontilhismo',
    pointilleElargi: 'Pontilhismo estendido',
    pointilleRiche: 'Pontilhismo rico',
    desPlein: 'Dados sólidos',
    briques: 'Tijolos',
    peinture: 'Pinceladas',
    peintureNuancee: 'Pinceladas matizadas',
    briquesNuancees: 'Tijolos matizados',
    pointDeCroix: 'Ponto cruz',
  },
  de: {
    motifs: 'Muster',
    motifsFin: 'Feine Muster',
    motifsTrait: 'Linienmuster',
    asciiElargi: 'Erweitertes ASCII',
    hachuresPleines: 'Volle Schraffur',
    pointille: 'Punktierung',
    pointilleElargi: 'Erweiterte Punktierung',
    pointilleRiche: 'Reiche Punktierung',
    desPlein: 'Volle Würfel',
    briques: 'Bausteine',
    peinture: 'Pinselstriche',
    peintureNuancee: 'Nuancierte Pinselstriche',
    briquesNuancees: 'Nuancierte Bausteine',
    pointDeCroix: 'Kreuzstich',
  },
};

const DIFFICULTY_LABELS: Registre = {
  en: { facile: 'Easy', moyen: 'Intermediate', avance: 'Advanced' },
  es: { facile: 'Fácil', moyen: 'Intermedio', avance: 'Avanzado' },
  pt: { facile: 'Fácil', moyen: 'Intermediário', avance: 'Avançado' },
  de: { facile: 'Einfach', moyen: 'Mittel', avance: 'Fortgeschritten' },
};

const INK_LABELS: Registre = {
  en: { encre: 'Ink', graphite: 'Graphite', sepia: 'Sepia', sousbois: 'Woodland' },
  es: { encre: 'Tinta', graphite: 'Grafito', sepia: 'Sepia', sousbois: 'Sotobosque' },
  pt: { encre: 'Tinta', graphite: 'Grafite', sepia: 'Sépia', sousbois: 'Sub-bosque' },
  de: { encre: 'Tinte', graphite: 'Graphit', sepia: 'Sepia', sousbois: 'Unterholz' },
};

/** Un id qu'un build ne connaît pas (un alphabet ajouté depuis en administration) garde son slug
 *  comme libellé plutôt que de disparaître : le bouton qui l'affiche mène encore quelque part, et
 *  le slug est le seul nom disponible pour lui. */
export function alphabetLabel(id: string): string {
  return ALPHABET_LABELS[lang]?.[id] ?? alphabetById(id)?.label ?? id;
}

export function difficultyLabel(key: string): string {
  return DIFFICULTY_LABELS[lang]?.[key] ?? diffLabel(key);
}

export function inkLabel(id: string): string {
  return INK_LABELS[lang]?.[id] ?? INKS.find((i) => i.id === id)?.label ?? id;
}

/**
 * Peuple tout ce qui est statique dans le HTML (index.html, p.html) au démarrage, à partir des
 * attributs `data-i18n*` posés sur le balisage. Le texte français qu'on trouve dans le HTML lui-
 * même reste un secours lisible si jamais ce script n'a pas encore tourné, mais cette fonction
 * l'écrase systématiquement : le dictionnaire est la vraie source, pas un filet de sécurité qu'on
 * espère ne jamais solliciter.
 *
 * Convention à quatre attributs, choisie pour ne JAMAIS avoir à restructurer un balisage écrit à
 * la main (index.html n'est pas passé au formateur — voir sa propre note en tête de fichier) :
 *  - `data-i18n` sur un nœud texte simple : `textContent` remplacé. Sur un élément dont le
 *    PREMIER enfant est un nœud texte suivi d'un élément (le motif `<span>Label<em>...</em>
 *    </span>` des lignes de réglages) : seul ce premier nœud texte est réécrit, l'enfant n'est
 *    pas touché et porte son propre `data-i18n`.
 *  - `data-i18n-html` : `innerHTML` remplacé — pour la seule valeur du dictionnaire qui contient
 *    du balisage EN PLEIN MILIEU d'une phrase (share.page.pitch), qu'on ne peut pas découper en
 *    deux clés sans casser l'ordre des mots dans une autre langue.
 *  - `data-i18n-label` : pose `title` ET `aria-label` d'un coup, pour les boutons icône où les
 *    deux sont aujourd'hui identiques.
 *  - `data-i18n-placeholder` : `placeholder` d'un champ de saisie.
 *  - `data-i18n-content` : attribut `content` d'un `<meta>`.
 */
export function applyStaticI18n(): void {
  document.documentElement.lang = lang;

  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const value = t(el.dataset.i18n as I18nKey);
    if (el.firstChild?.nodeType === Node.TEXT_NODE) el.firstChild.nodeValue = value;
    else el.textContent = value;
  }
  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n-html]')) {
    el.innerHTML = t(el.dataset.i18nHtml as I18nKey);
  }
  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n-label]')) {
    const value = t(el.dataset.i18nLabel as I18nKey);
    el.title = value;
    el.setAttribute('aria-label', value);
  }
  for (const el of document.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder]')) {
    el.placeholder = t(el.dataset.i18nPlaceholder as I18nKey);
  }
  for (const el of document.querySelectorAll<HTMLMetaElement>('[data-i18n-content]')) {
    el.content = t(el.dataset.i18nContent as I18nKey);
  }
}
